import { Link } from "react-router";

import { Card, PageHeader } from "@/components/ui";
import { SUPPORT_URL, TOKEN_SYMBOL } from "@/lib/config";
import { usePageMeta } from "@/lib/page-meta";

const FAQ = [
  {
    q: "Kenapa migrasi meminta dua tanda tangan?",
    a: "Tanda tangan pertama memberi kontrak izin exact amount untuk menggunakan token legacy. Tanda tangan kedua menjalankan migrasi dan mengirim token baru ke wallet yang sama. Izin token belum memindahkan token.",
  },
  {
    q: "Apakah saya perlu transfer token lama secara manual?",
    a: "Tidak. Kontrak resmi menarik jumlah yang sudah Anda izinkan saat transaksi migrasi. Permintaan transfer manual ke wallet atau alamat lain adalah tanda penipuan.",
  },
  {
    q: "Berapa rasio migrasinya?",
    a: "1:1 untuk saldo yang memenuhi aturan kontrak. Satu AsetQu Legacy menjadi satu AsetQu baru, dibatasi oleh batas maksimum per wallet dan status denylist on-chain.",
  },
  {
    q: "Apa yang terjadi jika transaksi gagal?",
    a: "Portal menunggu receipt jaringan sebelum menyatakan berhasil. Receipt gagal berarti migrasi tidak terkonfirmasi. Gunakan hash transaksi di halaman Verifikasi untuk memeriksa hasilnya.",
  },
  {
    q: "Bagaimana memastikan kontrak yang benar?",
    a: "Cocokkan alamat penuh pada transaction review dengan halaman Kontrak Resmi dan dengan alamat yang muncul di wallet. Jangan hanya membandingkan empat karakter awal.",
  },
  {
    q: "Apakah tim bisa mencetak token baru?",
    a: "Kontrak token baru tidak memiliki fungsi mint dan tidak memakai upgrade proxy. Seluruh supply dicetak sekali saat deployment dan dapat diverifikasi melalui source serta jaringan.",
  },
  {
    q: "Saldo saya ada di exchange. Apakah saya memakai portal ini?",
    a: "Tidak. Saldo di exchange mengikuti prosedur internal exchange. Ikuti pengumuman resmi exchange dan jangan memindahkan token legacy tanpa instruksi yang terverifikasi.",
  },
];

const RULES = [
  {
    number: "01",
    title: "ASETQU TIDAK PERNAH MEMINTA SEED PHRASE.",
    example: "Website, admin, support, dan email resmi tidak membutuhkan private key atau file keystore.",
    verify: "Jika sebuah halaman meminta kredensial wallet, tutup halaman tersebut.",
  },
  {
    number: "02",
    title: "PASTIKAN DOMAIN RESMI SEBELUM CONNECT.",
    example: "Domain tiruan sering mengganti satu huruf atau memakai subdomain yang menyesatkan.",
    verify: "Periksa address bar huruf per huruf sebelum membuka modal wallet.",
  },
  {
    number: "03",
    title: "COCOKKAN ALAMAT KONTRAK DI WALLET.",
    example: "Alamat spender untuk izin token harus sama dengan kontrak migrasi resmi.",
    verify: "Bandingkan alamat penuh dengan halaman Kontrak Resmi, bukan hanya label token.",
  },
  {
    number: "04",
    title: "IZIN TOKEN HANYA UNTUK EXACT AMOUNT.",
    example: "Migrasi 8.250 AsetQu hanya memerlukan izin 8.250, bukan unlimited approval.",
    verify: "Batalkan tanda tangan jika wallet menampilkan jumlah maksimum atau kontrak berbeda.",
  },
] as const;

const RED_FLAGS = [
  "Meminta transfer manual token legacy ke suatu wallet.",
  "Meminta unlimited approval atau setApprovalForAll.",
  "Meminta pembayaran untuk membuka atau mempercepat migrasi.",
  "Menghubungi Anda lebih dulu lewat DM dan mengaku sebagai admin.",
  "Menjanjikan bonus migrasi atau rasio lebih dari 1:1.",
  "Mengatakan transaksi berhasil sebelum receipt jaringan terkonfirmasi.",
];

export function SecurityPage() {
  usePageMeta({
    title: "Keamanan Migrasi",
    description: "Aturan keamanan dan FAQ untuk migrasi AsetQu.",
  });

  return (
    <div className="page-shell security-page">
      <PageHeader
        context="Security protocol"
        title="KEAMANAN SEBELUM SIGN"
        description={
          <>Gunakan aturan ini sebelum menghubungkan wallet atau menandatangani transaksi {TOKEN_SYMBOL}.</>
        }
        meta={
          <>
            <span>Self-custody</span>
            <span>Exact approval</span>
            <span>Contract first</span>
          </>
        }
      />

      <section className="security-rule-list" aria-label="Aturan keamanan utama">
        {RULES.map((rule) => (
          <article key={rule.number}>
            <span>{rule.number}</span>
            <div>
              <h2>{rule.title}</h2>
              <p>{rule.example}</p>
            </div>
            <aside>
              <strong>CARA VERIFIKASI</strong>
              <p>{rule.verify}</p>
            </aside>
          </article>
        ))}
      </section>

      <section className="security-check" aria-labelledby="security-check-title">
        <div>
          <p>CHECK BEFORE SIGNING</p>
          <h2 id="security-check-title">Empat data harus sama di portal dan wallet.</h2>
        </div>
        <ol>
          <li><span>✓</span><strong>Network</strong><p>BNB Smart Chain yang dikonfigurasi portal.</p></li>
          <li><span>✓</span><strong>Token</strong><p>AsetQu Legacy, bukan token tiruan.</p></li>
          <li><span>✓</span><strong>Amount</strong><p>Jumlah yang Anda pilih, bukan unlimited.</p></li>
          <li><span>✓</span><strong>Contract</strong><p>Alamat penuh sama dengan kontrak resmi.</p></li>
        </ol>
        <Link to="/contract-addresses">LIHAT KONTRAK RESMI <span aria-hidden>→</span></Link>
      </section>

      <section className="fraud-ledger" aria-labelledby="fraud-title">
        <header>
          <p>TRANSACTION BLOCKERS</p>
          <h2 id="fraud-title">Hentikan proses jika salah satu tanda ini muncul.</h2>
        </header>
        <ul>
          {RED_FLAGS.map((flag) => <li key={flag}><span aria-hidden>!</span>{flag}</li>)}
        </ul>
      </section>

      <Card title="Pertanyaan umum" className="faq-plate">
        <div className="faq-list">
          {FAQ.map((item) => (
            <details key={item.q}>
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </Card>

      <section className="support-record">
        <div>
          <p>PELAPORAN</p>
          <h2>Temukan situs palsu atau permintaan mencurigakan?</h2>
          <p>Simpan URL atau tangkapan layar. Jangan kirim seed phrase kepada siapa pun, termasuk support.</p>
        </div>
        {SUPPORT_URL ? (
          <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">BUKA KANAL DUKUNGAN ↗</a>
        ) : (
          <span>Kanal dukungan belum dikonfigurasi.</span>
        )}
      </section>
    </div>
  );
}
